import { host, gps_host } from "./host-objects";

// Режим:
//  - npm run build (production) → всегда относительные пути через Apache-прокси.
//  - npm run serve (development) → по значению host из host-objects.ts ("как раньше"):
//      host = "http://..." → напрямую на <host>:<port>
//      host = "/"          → относительные /<port> (режим прокси)
//    Текущий хост для dev выставляется в src/config/host-objects.ts (export const host).
const isProd = process.env.NODE_ENV === "production";
const isUrl = (h: string) => /^https?:\/\//i.test(h);

const direct = !isProd && isUrl(host);
const directGps = !isProd && isUrl(gps_host);

const port = (num: number, useGps = false): string => {
  if (useGps) return directGps ? `${gps_host}:${num}` : `/${num}`;
  return direct ? `${host}:${num}` : `/${num}`;
};
const socketPath = (devPort: number, prodName: string): string =>
  direct ? `${host}:${devPort}` : `/socket/${prodName}`;

// Префиксы портов: в dev — полный URL <host>:<port>, в prod — /<port> через прокси
const PORTS = {
  auth: port(2999),
  gps: port(3000, true),
  weather: port(3001),
  order: port(3002),
  channels: port(3003),
  routeInfo: port(3004),
  multimedia: port(3005),
  dangisWiki: port(3006),
  hotelInfo: port(3007),
  cruiseOffers: port(3009),
  pics: port(3010),
  excursions: port(3011),
  menu: port(3012),
  gallerys: port(3013),
  configuration: port(3014),
  audio: port(3015),
  ais: port(3016),
  skdu: port(3019),
};

export const getWebImage = direct ? `${host}:8081` : "/images";

// ========== AIS ==========
export const ais = {
  mainAPI: PORTS.ais + "/api/ships",
};

// ========== serversAPI (для обратной совместимости) ==========
export const serversAPI = {
  auth: PORTS.auth + "/api/auth",
  gps: PORTS.gps + "/api/gps",
  weather: PORTS.weather + "/weather", // у weather нет /api
  order: PORTS.order + "/api/order",
  channels: PORTS.channels + "/api/channels",
  routeInfo: PORTS.routeInfo + "/api/routeInfo",
  multimedia: PORTS.multimedia + "/api/multimedia", // ! но мы не используем этот объект напрямую
  dangisWiki: PORTS.dangisWiki + "/api/dangisWiki",
  hotelInfo: PORTS.hotelInfo + "/api/hotelInfo",
  cruiseOffers: PORTS.cruiseOffers + "/api/cruiseOffers",
  pics: PORTS.pics + "/api/pics",
  excursions: PORTS.excursions + "/api/excursions",
  menu: PORTS.menu + "/api/menu",
  gallerys: PORTS.gallerys + "/api/gallerys",
  configuration: PORTS.configuration + "/api/configuration",
  ais: PORTS.ais + "/api/ais",
  audio: PORTS.audio + "/api/audio",
};

// ========== AUTH ==========
export const auth = {
  getAPI: PORTS.auth + "/api/methods",
  login: PORTS.auth + "/api/auth/login",
  logout: PORTS.auth + "/api/auth/logout",
  pubkey: PORTS.auth + "/api/auth/pubkey",
  challenge: PORTS.auth + "/api/auth/challenge",
  tvTicket: PORTS.auth + "/api/auth/registration/tv_ticket",
  refresh: PORTS.auth + "/api/auth/refresh",
  access: PORTS.auth + "/api/auth/alive",
  getUser: PORTS.auth + "/api/auth/users",
  user: PORTS.auth + "/api/auth/user",
  forseUser: PORTS.auth + "/api/auth/users/force",
  restoreUser: PORTS.auth + "/api/auth/users/recycler",
  getRoutes: PORTS.auth + "/api/routes",
  tvRegistration: PORTS.auth + "/api/auth/registration/tv",
  support: PORTS.auth + "/api/support",
  registration: PORTS.auth + "/api/auth/registration",
  registration_tv: PORTS.auth + "/api/auth/registration/tv",
  registration_tablet: PORTS.auth + "/api/auth/registration/tablet",
  permissionsLinks: PORTS.auth + "/api/permissions/links",
  messages: PORTS.auth + "/api/auth/status/many",
  message: PORTS.auth + "/api/auth/status",
  internet_admin: PORTS.auth + "/api/auth/internet/admin",
  internet_getstatus: PORTS.auth + "/api/mikrotik/getstatus",

  // Room
  room: PORTS.auth + "/api/rooms",
  restore_room: PORTS.auth + "/api/rooms/restore",
  force_room: PORTS.auth + "/api/rooms/force",
  room_status: PORTS.auth + "/api/rooms/status",
  restore_room_status: PORTS.auth + "/api/rooms/status/restore",
  force_room_status: PORTS.auth + "/api/rooms/status/force",
  room_type: PORTS.auth + "/api/rooms/type",
  restore_room_type: PORTS.auth + "/api/rooms/type/restore",
  force_room_type: PORTS.auth + "/api/rooms/type/force",
  room_level: PORTS.auth + "/api/rooms/level",
  restore_room_level: PORTS.auth + "/api/rooms/level/restore",
  force_room_level: PORTS.auth + "/api/rooms/level/force",

  // Guests
  guests: PORTS.auth + "/api/guests",
  guests_many: PORTS.auth + "/api/guests/many",
  restore_guest: PORTS.auth + "/api/guests/restore",
  force_guest: PORTS.auth + "/api/guests/force",
  bitrix: PORTS.auth + "/api/guests/passangers",
  guest: {
    many: PORTS.auth + "/api/guests/many",
    many_new: PORTS.auth + "/api/guests/many/new",
    export_phones: PORTS.auth + "/api/guests/export/phones",
  },
  internet: {
    restoring_internet_access: PORTS.auth + "/api/auth/internet/restore",
  },

  // Virtual rooms
  virtual_rooms: PORTS.auth + "/api/virtual_rooms",
  restore_virtual_rooms: PORTS.auth + "/api/guests/restore",
  force_virtual_rooms: PORTS.auth + "/api/guests/force",
  virtual_room: {
    bulk: PORTS.auth + "/api/virtual_rooms/bulk",
    many: PORTS.auth + "/api/virtual_rooms/many",
  },

  // Restaurants
  restaurant: PORTS.auth + "/api/restaurant",
  restaurant_plan: PORTS.auth + "/api/restaurant",
  restaurant_recycler: PORTS.auth + "/api/restaurant/recycler",
  restaurant_force: PORTS.auth + "/api/restaurant/force",

  // Consents
  consents: PORTS.auth + "/api/consents",
};

// ========== GPS ==========
export const gps = {
  mainAPI: PORTS.gps + "/api/gps",
  flight: PORTS.gps + "/api/fly",
  geocoder: PORTS.gps + "/api/geocode",
  osmTile: PORTS.gps + "/api/osm/osmtile?z={z}&x={x}&y={y}",
  config: PORTS.gps + "/api/config",
};

// ========== WEATHER ==========
export const weather = {
  mainApi: PORTS.weather + "/weather",
  mode: PORTS.weather + "/mode",
  rugeocoder: PORTS.weather + "/rugeocoder.json",
  engeocoder: PORTS.weather + "/engeocoder.json",
};

// ========== ORDER ==========
export const order = {
  mainAPI: PORTS.order + "/api/order",
  reason: PORTS.order + "/api/reason",
  reason_restore: PORTS.order + "/api/reason/restore",
  reason_forse: PORTS.order + "/api/reason/forse",
};

// ========== CHANNELS ==========
export const channels = {
  mainAPI: PORTS.channels + "/api/channels/",
  forceDelete: PORTS.channels + "/api/channels/force",
  allChannels: PORTS.channels + "/api/channels/all",
  fullChannels: PORTS.channels + "/api/channels/all",
  byContentType: PORTS.channels + "/api/channels/content_type",
  restore: PORTS.channels + "/api/channels/restore",
  content_type: PORTS.channels + "/api/channels/content_type",
  content_type_restore: PORTS.channels + "/api/channels/content_type/restore",
  force_delete_content_type:
    PORTS.channels + "/api/channels/content_type/force",
};

// ========== ROUTE INFO ==========
export const routeInfo = {
  citys: PORTS.routeInfo + "/api/citys",
  cityForce: PORTS.routeInfo + "/api/citys/force",
  cityRestore: PORTS.routeInfo + "/api/citys/restore",
  routes: PORTS.routeInfo + "/api/routes",
  config: PORTS.routeInfo + "/api/routes/config",
  copy: PORTS.routeInfo + "/api/citys/auto/copy/",
  auto: PORTS.routeInfo + "/api/routes/auto",
  v2: {
    routes: PORTS.routeInfo + "/api/v2/routes",
    cities: PORTS.routeInfo + "/api/v2/cities",
    routes_restore: PORTS.routeInfo + "/api/v2/routes/restore",
    cities_restore: PORTS.routeInfo + "/api/v2/cities/restore",
    routes_force: PORTS.routeInfo + "/api/v2/routes/force",
    cities_force: PORTS.routeInfo + "/api/v2/cities/force",
  },
};

// ========== MULTIMEDIA ==========
export const multimedia = {
  films: PORTS.multimedia + "/api/films/",
  categorys: PORTS.multimedia + "/api/categorys",
  categorysRestore: PORTS.multimedia + "/api/categorys/restore",
  forceCategory: PORTS.multimedia + "/api/categorys/force",
  meta: PORTS.multimedia + "/api/films/meta",
  movie: PORTS.multimedia + "/api/movie/",
  recucler: PORTS.multimedia + "/api/films/recycler",
  restore_movie: PORTS.multimedia + "/api/films/recycler/",
  forceDelete: PORTS.multimedia + "/api/films",
  socket: socketPath(3020, "multimedia"), // dev: host:3020 | prod: /socket/multimedia
  export: {
    excel: PORTS.auth + "/api/guests/export/excel",
  },
};

// ========== AUDIO ==========
export const audio = {
  songs: PORTS.audio + "/api/songs",
  songsRestore: PORTS.audio + "/api/songs/restore",
  songsForce: PORTS.audio + "/api/songs/force",
  songsSRC: PORTS.audio + "/api/stream",
  songsSRCFs: PORTS.audio + "/api/fs_stream",
  id3Stream: PORTS.audio + "/api/id3_stream",
  id3Playlists: PORTS.audio + "/api/id3/playlists",
  id3Tracks: PORTS.audio + "/api/id3/tracks",

  categorys: PORTS.audio + "/api/categorys",
  categorysRestore: PORTS.audio + "/api/categorys/restore",
  categoryForce: PORTS.audio + "/api/categorys/force",

  genres: PORTS.audio + "/api/genres",
  genresRestore: PORTS.audio + "/api/genres/restore",
  genresForce: PORTS.audio + "/api/genres/force",

  singers: PORTS.audio + "/api/singers",
  singersRestore: PORTS.audio + "/api/singers/restore",
  singersForce: PORTS.audio + "/api/singers/force",

  audioSocket: socketPath(3021, "audio"),
  audio_fs_socket: socketPath(3022, "audio_fs"),
  audioMusicSocket: socketPath(3026, "audio_music"),
  audio_fs: PORTS.audio + "/api/fs_audio",
};

// ========== AUDIOBOOKS ==========
export const audiobooks = {
  books: PORTS.audio + "/api/books",
  stream: PORTS.audio + "/api/books/stream",
  audiobooksSocket: socketPath(3025, "audiobooks"),
  posterUpload: PORTS.pics + "/api/upload/new?path=audio/audiobooks/poster",
};

// ========== ROOM SERVICE (относится к order) ==========
export const roomService = {
  base: PORTS.order + "/api/service",
  adminAll: PORTS.order + "/api/service/admin/all",
  adminByRoom: PORTS.order + "/api/service/admin/by-room",
  admin: PORTS.order + "/api/service/admin",
  my: PORTS.order + "/api/service/my",
  health: PORTS.order + "/api/service/health/check",
};

// ========== DANGIS WIKI ==========
export const dangisWiki = {
  mainAPI: PORTS.dangisWiki + "/api/wiki",
  restore: PORTS.dangisWiki + "/api/wiki/recycler",
  wikiGps: PORTS.dangisWiki + "/api/wiki/gps",
  getAllDots: PORTS.dangisWiki + "/api/wiki/all",
  force: PORTS.dangisWiki + "/api/wiki/force",

  categorys: {
    main: PORTS.dangisWiki + "/api/category",
    restore: PORTS.dangisWiki + "/api/category/restore",
    force: PORTS.dangisWiki + "/api/category/force",
  },
  config: { main: PORTS.dangisWiki + "/api/config" },
};

// ========== HOTEL INFO ==========
export const hotelInfo = {
  mainAPI: PORTS.hotelInfo + "/api/info",
  force: PORTS.hotelInfo + "/api/info/force",
  restore: PORTS.hotelInfo + "/api/info/restore",
};

// ========== STAFF ==========
export const staff = {
  staff: PORTS.auth + "/api/staff",
  staffForce: PORTS.auth + "/api/staff/force",
  staffRestore: PORTS.auth + "/api/staff/restore",
  role: PORTS.auth + "/api/role",
  roleForce: PORTS.auth + "/api/role/force",
  roleRestore: PORTS.auth + "/api/role/restore",
};

// ========== CRUISE OFFERS ==========
export const cruiseOffers = {
  mainAPI: PORTS.cruiseOffers + "/api/cruise",
  cruiseImages: PORTS.cruiseOffers + "/api/cruise/images",
  ships: PORTS.cruiseOffers + "/api/cruise/ships",
  shipList: PORTS.cruiseOffers + "/api/ships",
  citys: PORTS.cruiseOffers + "/api/citys",
  shipCodes: PORTS.cruiseOffers + "/api/cruise/ships",
};

// ========== PICS ==========
export const pics = {
  mainAPI: PORTS.pics + "/api/pics", // корень
  upload: PORTS.pics + "/api/upload",
  newupload: PORTS.pics + "/api/upload/new",
  uploadPdf: PORTS.pics + "/api/upload/pdf-upload",
  pdf_to_png: PORTS.pics + "/api/upload/pdf",
  download: PORTS.pics + "/api/download",
  cinema: PORTS.pics + "/api/cinema",
  timetable: PORTS.pics + "/api/timetable",
  gallery: PORTS.gallerys + "/api/images",
  sections: PORTS.pics + "/api/sections",
};

// ========== EXCURSIONS ==========
export const excursions = {
  mainAPI: PORTS.excursions + "/api/excursions",
  excursionForse: PORTS.excursions + "/api/excursions/force",
  excursionRestore: PORTS.excursions + "/api/excursions/restore",
  category: PORTS.excursions + "/api/category",
  categoryRestore: PORTS.excursions + "/api/category/restore",
  categoryForse: PORTS.excursions + "/api/category/force",
};

// ========== MENU ==========
export const menu = {
  products: PORTS.menu + "/api/menu",
  force: PORTS.menu + "/api/menu/force",
  categorys: PORTS.menu + "/api/category",
  categoryRestore: PORTS.menu + "/api/category/restore",
  categoryForce: PORTS.menu + "/api/category/force",
  restore: PORTS.menu + "/api/menu/restore",
  forceDelete: PORTS.menu + "/api/menu/force",
  bars: PORTS.menu + "/api/bars",
  barRestore: PORTS.menu + "/api/bars/restore",
  complex_menu: PORTS.menu + "/api/restaurant",
  complex_menu_restore: PORTS.menu + "/api/restaurant/restore",
  complex_menu_force: PORTS.menu + "/api/restaurant/force",
};

// ========== GALLERYS ==========
export const gallerys = {
  mainAPI: PORTS.gallerys + "/api/gallery",
  categotys: PORTS.gallerys + "/api/categorys",
  ftp: PORTS.gallerys + "/api/gallery/dirpull",
  formats: PORTS.gallerys + "/api/format",
  categotysRestore: PORTS.gallerys + "/api/categorys/restore",
  formatRestore: PORTS.gallerys + "/api/format/restore",
  force: PORTS.gallerys + "/api/gallery/force",
  categotysForce: PORTS.gallerys + "/api/categorys/force",
  formatForce: PORTS.gallerys + "/api/format/force",
};

// ========== SKDU (Server_SmartHome) ==========
export const skdu = {
  health: PORTS.skdu + "/api/health",
  serverUrl: PORTS.skdu + "/api/server-url",
};

// ========== CONFIGURATION ==========
export const configuration = {
  telemetry: PORTS.configuration + "/api/telemetry",
  telemetryRestart: PORTS.configuration + "/api/telemetry/restart",
  telemetryStop: PORTS.configuration + "/api/telemetry/stop",
  mainAPI: PORTS.configuration + "/api/config",
  currency: {
    main: PORTS.configuration + "/api/currency",
    force: PORTS.configuration + "/api/currency/force",
    restore: PORTS.configuration + "/api/currency/restore",
  },
  hello: {
    main: PORTS.configuration + "/api/hello",
    force: PORTS.configuration + "/api/hello/force",
    restore: PORTS.configuration + "/api/hello/restore",
  },
  pages: {
    main: PORTS.configuration + "/api/pages",
    force: PORTS.configuration + "/api/pages/force",
  },
};
